import { Fragment, type ReactNode } from 'react'
import { parseRichText, type RichNode } from '@/lib/render/rich-text'
import { parseMathText, speakMathSegment } from '@unlimitedtests/core'
import { unescapeDisplayText } from '@/lib/render/display-escapes'

// Render a plain-text leaf with caret exponents turned into superscripts
// (reuses the MathText parser so explanations get x^2 → x² too) AND newlines
// turned into <br/>. AI-generated explanations put each step/equation on its
// own line; without the newline→<br/> pass they collapse into one run-on
// paragraph (each \n rendered as a space). A blank line (\n\n) becomes two
// <br/> — a visible section break.
function renderLeaf(text: string, keyPrefix: string): ReactNode {
  return parseMathText(text).map((seg, i) => {
    const segKey = `${keyPrefix}-${i}`
    // See MathText.tsx: role="img" + aria-label is the only spoken form
    // that adds no text node, so textContent stays byte-identical.
    if (seg.sup)
      return (
        <sup key={segKey} role="img" aria-label={speakMathSegment(seg.text)}>
          {seg.text}
        </sup>
      )
    const lines = seg.text.split('\n')
    return (
      <Fragment key={segKey}>
        {lines.map((line, k) => (
          <Fragment key={k}>
            {k > 0 && <br />}
            {line}
          </Fragment>
        ))}
      </Fragment>
    )
  })
}

function renderNodes(nodes: RichNode[], keyPrefix: string): ReactNode {
  return nodes.map((node, i) => {
    const key = `${keyPrefix}-${i}`
    if (node.type === 'text') return <Fragment key={key}>{renderLeaf(node.text, key)}</Fragment>
    if (node.type === 'bold') return <strong key={key}>{renderNodes(node.children, key)}</strong>
    return <em key={key}>{renderNodes(node.children, key)}</em>
  })
}

/**
 * Renders AI-generated explanation text: Markdown emphasis (**bold**,
 * *italic*) PLUS caret-exponent superscripts (x^2 → x²). Presentation-only.
 *
 * Use this for explanations, which arrive as Markdown. Keep <MathText>
 * (carets only) for official stems/choices, which must not interpret a
 * stray asterisk as emphasis. Drop-in for `{explanation}`:
 *   <RichText text={explanation} />
 */
export default function RichText({
  text,
  children,
  className,
}: {
  text?: string
  children?: string
  className?: string
}) {
  // Leaked source escapes ("\$85", a literal "\n") are never meant to be
  // seen — see lib/render/display-escapes.ts.
  const raw = unescapeDisplayText(text ?? children ?? '')
  return <span className={className}>{renderNodes(parseRichText(raw), 'rt')}</span>
}
